/**
 * The classic template's reading of an invitation payload (the shape of
 * GET /api/events/:eventId/public): which sections it draws, in order, and
 * what each one shows.
 *
 * Pure — the payload and an i18next `t` in, plain data out — so the guests'
 * page and the editor's preview draw from exactly the same decisions, and so
 * those decisions can be tested without a browser.
 */
import { TEMPLATES } from "../../registry.js";
import { slotTexts } from "../../slots.js";

const TEMPLATE_ID = "classic";
const TOGGLEABLE = new Set(
  TEMPLATES[TEMPLATE_ID].sections.filter((s) => s.toggleable).map((s) => s.key),
);

const STORY_LABEL_KEYS = {
  "where-it-all-began": "story.blockKeys.whereItAllBegan",
  "a-love-takes-shape": "story.blockKeys.loveShape",
  "a-promise-between-them": "story.blockKeys.promiseBetween",
  "the-beginning-of-forever": "story.blockKeys.beginningOfForever",
};

const NO_PLACEHOLDERS = Object.freeze({
  names: false,
  location: false,
  ceremony: false,
  reception: false,
  schedule: false,
});

/**
 * @param {object|null} payload  the public invitation payload; null draws nothing
 * @param {Function} t           i18next `t` in the language to draw
 * @param {{ preview?: boolean }} [options]  the editor's preview marks missing
 *   mandatory details with placeholders instead of leaving them out
 */
export function buildClassicModel(payload, t, { preview = false } = {}) {
  if (!payload) {
    return {
      sections: [],
      layoutKey: "",
      storySlides: [],
      scheduleItems: [],
      venueItems: [],
      participantNames: [],
      lines: { interludeTop: false, storyTop: false, quoteTop: false },
      placeholders: NO_PLACEHOLDERS,
      texts: slotTexts(t, TEMPLATE_ID, null, undefined),
    };
  }
  const event = payload;

  // Only sections this template lets the couple turn off can be hidden —
  // the same rule the API stores by, applied again to the editor's drafts.
  const hidden = new Set(
    (Array.isArray(event.invitation?.hiddenSections) ? event.invitation.hiddenSections : [])
      .filter((key) => TOGGLEABLE.has(key)),
  );

  // StorySlider slides — map event.storyBlocks to the { year, title, description, image, alt } shape.
  // Each block carries an optional couple-uploaded `imageUrl` (presigned by the
  // public endpoint); fall back to the positional default story-{idx+1}.jpg so
  // older couples / unset slides keep the bundled experience.
  const storyLabel = (key) => (STORY_LABEL_KEYS[key] ? t(STORY_LABEL_KEYS[key]) : key);
  const storySlides =
    event.storyBlocks
      ?.filter((b) => b.content?.trim())
      .map((b, idx) => ({
        year: b.year || "",
        title: storyLabel(b.key),
        description: b.content,
        image: b.imageUrl || `/assets/images/stories/story-${idx + 1}.jpg`,
        alt: storyLabel(b.key),
      })) || [];

  // ScheduleList items — map event.scheduleEntries to { title, description }
  const scheduleItems =
    event.scheduleEntries?.map((e) => ({
      title: `${e.time} — ${e.title}`,
      description: e.description || "",
    })) || [];

  // VenuesGrid venue items — build from churchLocation / venueName.
  // Each venue uses the couple's uploaded photo when present; falls back to
  // the bundled default if they haven't customized this slot. The API
  // returns ceremonyImage/receptionImage as presigned URLs, which is fine
  // since the invite page refetches on every visit.
  const venueItems = [];
  if (event.churchLocation) {
    venueItems.push({
      image: event.ceremonyImage || "/assets/images/the-ceremony.jpg",
      number: "01",
      title: t("venues.items.ceremony.title"),
      description: t("venues.items.ceremony.descriptionAlt"),
      location: event.churchLocation,
      mapUrl: event.churchMapUrl || "",
    });
  }
  if (event.venueName) {
    venueItems.push({
      image: event.receptionImage || "/assets/images/the-celebration.jpg",
      number: String(venueItems.length + 1).padStart(2, "0"),
      title: t("venues.items.celebration.title"),
      description: t("venues.items.celebration.descriptionAlt"),
      location: event.venueName,
      mapUrl: event.venueMapUrl || "",
    });
  }

  // VenueDetailSpecs participantNames — build from families + honor attendants
  const participantNames = [];
  if (event.groomFamily?.length)
    participantNames.push({
      label: t("participants.groomFamily"),
      value: event.groomFamily.join("\n"),
    });
  if (event.brideFamily?.length)
    participantNames.push({
      label: t("participants.brideFamily"),
      value: event.brideFamily.join("\n"),
    });
  if (event.bestMen?.length)
    participantNames.push({
      label:
        event.bestMen.length === 1 ? t("participants.bestMan") : t("participants.bestMen"),
      value: event.bestMen.join("\n"),
    });
  if (event.maidsOfHonor?.length)
    participantNames.push({
      label:
        event.maidsOfHonor.length === 1
          ? t("participants.maidOfHonor")
          : t("participants.maidsOfHonor"),
      value: event.maidsOfHonor.join("\n"),
    });

  // When each section is drawn (INVITATION-EDITOR-SPEC.md §3). The mandatory
  // ones are guaranteed for guests by the readiness gate in pages/RSVP.jsx;
  // the editor's preview draws them regardless, with placeholders.
  //
  // `gifts` mirrors the BE/dashboard rule in invitation-readiness.mjs:
  // message + IBAN + holder are all required (registry URL alone is not
  // enough). Don't relax it here without updating invitation-readiness.mjs
  // in lockstep.
  //
  // Participants are never drawn for a pure baptism, even if an event that
  // used to be a wedding still holds families or honor attendants — the
  // editor doesn't offer that section for baptisms.
  const drawn = {
    hero: true,
    invitation: true,
    participants: event.eventType !== "baptism" && participantNames.length > 0,
    interlude: true,
    story: storySlides.length > 0,
    quote: true,
    venues: true,
    schedule: true,
    moments: true,
    gifts: !!(
      event.giftMessage?.trim() &&
      event.giftIban?.trim() &&
      event.giftIbanHolder?.trim()
    ),
    rsvp: (event.rsvpSectionCount ?? 0) > 0,
  };
  const sections = TEMPLATES[TEMPLATE_ID].sections
    .map((s) => s.key)
    .filter((key) => drawn[key] && !hidden.has(key));

  // Neighbouring sections draw the connecting lines between them, so a line
  // depends on the section drawn just above it — never on what is merely
  // stored. Without this, lines stack or dangle as sections come and go.
  const above = (key) =>
    sections.includes(key) ? sections[sections.indexOf(key) - 1] : null;
  const lines = {
    // The interlude's tall top line (and its block's top rule) reaches up into
    // the section above; the design only ever drew it under the participants.
    interludeTop: above("interlude") === "participants",
    // The story was only ever drawn under the interlude, whose bottom line
    // leads down into it; anywhere else it draws that line itself.
    storyTop: !!above("story") && above("story") !== "interlude",
    // Under the interlude, whose own bottom line already leads down into the
    // quote, the quote's short top line would stutter across the gap.
    quoteTop: !!above("quote") && above("quote") !== "interlude",
  };

  const placeholders = preview
    ? {
        names: !(event.partner1FirstName?.trim() && event.partner2FirstName?.trim()),
        location: !event.location?.trim(),
        ceremony: !event.churchLocation,
        reception: !event.venueName,
        schedule: scheduleItems.length === 0,
      }
    : NO_PLACEHOLDERS;

  return {
    sections,
    // What the Webflow runtime is bound to: the sections on the page and the
    // story slides. Anything else (words, photos) changes in place.
    layoutKey: `${sections.join(",")}|${storySlides.length}`,
    storySlides,
    scheduleItems,
    venueItems,
    participantNames,
    lines,
    placeholders,
    texts: slotTexts(t, TEMPLATE_ID, event.invitation, event.eventType),
  };
}
